import { useCallback, useMemo } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useAttachmentStore } from 'stores/attachmentStore';
import { findNavigatorWithRoute } from 'utils/navigationUtils';
import { formatFileNameForUI } from 'utils/attachmentUtils';
import type { RootNavProps } from 'types/navigation';
import { useUiStore } from 'stores/uiStore';

type Options = { pressable?: boolean };

type AttachmentRowModel = {
  title: string;
  displayName: string;
  fullName: string;
  count: number;
  isInteractive: boolean;
  handlePress: () => void;
};

export const useAttachmentRow = ({
  pressable = true,
}: Options = {}): AttachmentRowModel => {
  const { t } = useTranslation();
  const navigation = useNavigation<RootNavProps>();
  const ctxId = useAttachmentStore((state) => state.currentAttachmentContext);
  const all = useAttachmentStore((state) => state.attachments);
  const readOnly = useAttachmentStore((state) => state.attachmentReadOnly);

  const uploaded = useMemo(() => {
    if (!ctxId) return [];
    return all.filter((x) => x.contextId === ctxId && !!x.uploadUid);
  }, [all, ctxId]);
  const count = uploaded.length;

  const title = t('longTermParking.attachment.title');
  let fullName = t('longTermParking.attachment.add');
  if (count === 1) {
    const f = uploaded[0];
    fullName =
      f?.uploadFileName ??
      f?.name ??
      t('longTermParking.attachment.fallbackName');
  } else if (count > 1) {
    fullName = t('longTermParking.attachment.attachmentsCount', { count });
  }
  const displayName = formatFileNameForUI(fullName);

  const handlePress = useCallback(() => {
    if (!pressable) return;
    if (count === 0) {
      if (readOnly) return;
      useUiStore.getState().setSheetModal('addAttachment');
      return;
    }
    if (count > 1) {
      navigation.navigate('attachmentsList');
      return;
    }
    useAttachmentStore.getState().setSelectedAttachment(uploaded[0] ?? null);
    requestAnimationFrame(() => {
      const nav = findNavigatorWithRoute(navigation, 'attachmentPreview');
      nav?.navigate?.('attachmentPreview');
    });
  }, [pressable, readOnly, count, uploaded, navigation]);

  const isInteractive = pressable && (count > 0 || !readOnly);

  return { title, displayName, fullName, count, isInteractive, handlePress };
};
